import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, QrCode, Disc3, Users, Monitor, Square, CalendarClock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { API_URL } from '../utils/api';
import { useConnectionStatus, CONNECTION_STATES } from '../hooks/useConnectionStatus';
import { ConnectionStatusIndicator } from '../components/ConnectionStatusIndicator';

/**
 * DJProjectorPage - TV/Projector Display for DJ Live with aspect ratio toggle
 * 
 * Layout fixes (Phase DL-2):
 * - QR code positioned at TOP with flex-shrink-0
 * - Queue section uses flex-1 with overflow-y-auto for independent scrolling
 * 
 * Performance fixes (Phase DL-2):
 * - Data diffing prevents unnecessary re-renders
 * 
 * Network Recovery (Phase DL-4):
 * - Connection status indicator (Connected/Reconnecting/Disconnected)
 * - Automatic recovery with exponential backoff
 * - Safe display of last known data during disconnection
 */
export default function DJProjectorPage({ mode = 'club' }) {
  const { clubSlug, djSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const entitySlug = mode === 'dj' ? djSlug : clubSlug;
  const [sessionData, setSessionData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Refs for data diffing to prevent unnecessary re-renders
  const lastSessionDataRef = useRef(null);
  const lastScheduleDataRef = useRef(null);
  
  // Aspect ratio from URL param, default to 4:3
  const aspectRatio = searchParams.get('ratio') || '4:3';
  const is16by9 = aspectRatio === '16:9';

  // Toggle aspect ratio
  const toggleAspectRatio = () => {
    const newRatio = is16by9 ? '4:3' : '16:9';
    setSearchParams({ ratio: newRatio });
  };

  // API base URL based on mode
  const apiBase = mode === 'dj' 
    ? `${API_URL}/api/djs/${entitySlug}/dj`
    : `${API_URL}/api/clubs/${entitySlug}/dj`;

  // Fetch function for connection status hook
  const fetchData = useCallback(async () => {
    const response = await fetch(`${apiBase}/live`);
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(mode === 'dj' ? 'DJ not found' : 'Club not found');
      }
      throw new Error('Failed to load session');
    }
    const data = await response.json();
    
    // Also fetch schedule
    try {
      const scheduleRes = await fetch(`${apiBase}/schedule`);
      if (scheduleRes.ok) {
        const schedData = await scheduleRes.json();
        const schedStr = JSON.stringify(schedData);
        if (schedStr !== lastScheduleDataRef.current) {
          lastScheduleDataRef.current = schedStr;
          setScheduleData(schedData);
        }
      }
    } catch (schedErr) {
      console.warn('Schedule fetch failed:', schedErr);
    }
    
    return data;
  }, [apiBase, mode]);

  // Handle data received from connection status hook
  const handleDataReceived = useCallback((data, isFirstFetch) => {
    if (data.active) {
      const dataStr = JSON.stringify(data);
      if (dataStr !== lastSessionDataRef.current) {
        lastSessionDataRef.current = dataStr;
        setSessionData(data);
      }
      setError(null);
    } else {
      setError('No active session');
    }
    setLoading(false);
  }, []);

  // Handle data reconciliation after recovery
  const handleReconcile = useCallback((previousData, newData) => {
    console.log('[DJProjectorPage] Reconciling data after recovery');
    if (newData.active) {
      const dataStr = JSON.stringify(newData);
      lastSessionDataRef.current = dataStr;
      setSessionData(newData);
      setError(null);
    }
  }, []);

  // Use connection status hook for managed polling and recovery
  const {
    connectionStatus,
    lastSuccessfulUpdate,
    isStale,
    isRecovering,
    resetConnection
  } = useConnectionStatus({
    fetchFn: fetchData,
    onDataReceived: handleDataReceived,
    onReconcile: handleReconcile,
    pollInterval: 3000,
    config: {
      maxRetries: 2,
      disconnectedThreshold: 5,
      staleDataThreshold: 15000,
    }
  });

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Get the live page URL for QR code based on mode
  const livePageUrl = mode === 'dj'
    ? `${window.location.origin}/dj/${entitySlug}/live`
    : `${window.location.origin}/club/${entitySlug}/dj-live`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Disc3 className="w-20 h-20 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Disc3 className="w-24 h-24 text-slate-600 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-2">Session Not Available</h1>
          <p className="text-xl text-slate-400">{error || 'No active session'}</p>
        </div>
      </div>
    );
  }

  // Map the API response to display data
  const entity = sessionData.club || sessionData.dj || {};
  const nowPlaying = sessionData.current_dance;
  const upNext = (sessionData.upcoming_dances || []).slice(0, is16by9 ? 4 : 3);
  const playedCount = sessionData.played_count || 0;
  const requestCount = sessionData.requests?.length || 0;
  const announcement = sessionData.announcement;
  
  // Get current and upcoming schedule items (filter out past items)
  const getCurrentScheduleItems = () => {
    if (!scheduleData?.schedule_enabled || !scheduleData?.schedule_items?.length) {
      return [];
    }
    
    const now = currentTime.getHours() * 60 + currentTime.getMinutes();
    
    return scheduleData.schedule_items
      .map(item => {
        const [hours, minutes] = item.time.split(':').map(Number);
        const itemMinutes = hours * 60 + minutes;
        return { ...item, totalMinutes: itemMinutes };
      })
      .filter(item => item.totalMinutes >= now - 5) // Show items from 5 min ago onwards
      .slice(0, 4); // Show max 4 items
  };
  
  const scheduleItems = getCurrentScheduleItems();
  const hasSchedule = scheduleData?.schedule_enabled && scheduleItems.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 text-white overflow-hidden">
      {/* Connection Status Indicator for TV displays */}
      <ConnectionStatusIndicator 
        status={connectionStatus}
        isRecovering={isRecovering}
        isStale={isStale}
        variant="tv"
        lastUpdate={lastSuccessfulUpdate}
      />

      {/* Aspect Ratio Toggle Button - Top Right */}
      <button
        onClick={toggleAspectRatio}
        className="fixed top-4 right-4 z-50 bg-slate-800/80 hover:bg-slate-700/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-slate-600/50 flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
        title="Toggle aspect ratio"
      >
        {is16by9 ? (
          <>
            <Monitor className="w-4 h-4" />
            <span>16:9</span>
          </>
        ) : (
          <>
            <Square className="w-4 h-4" />
            <span>4:3</span>
          </>
        )}
      </button>

      {/* Announcement Ticker */}
      {announcement && (
        <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 py-2 overflow-hidden relative z-20">
          <div className="animate-marquee whitespace-nowrap">
            <span className="text-lg font-bold text-white mx-8">📢 {announcement}</span>
            <span className="text-lg font-bold text-white mx-8">📢 {announcement}</span>
            <span className="text-lg font-bold text-white mx-8">📢 {announcement}</span>
            <span className="text-lg font-bold text-white mx-8">📢 {announcement}</span>
          </div>
        </div>
      )}

      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-purple-600/10 to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-violet-600/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Main container with aspect ratio */}
      <div 
        className={`relative z-10 mx-auto ${announcement ? 'h-[calc(100vh-40px)]' : 'h-screen'}`}
        style={{
          maxWidth: is16by9 ? '100%' : 'calc(100vh * 4 / 3)',
          aspectRatio: is16by9 ? 'auto' : '4 / 3'
        }}
      >
        <div className="h-full flex flex-col p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {entity.image_url ? (
                <img 
                  src={entity.image_url} 
                  alt={entity.name} 
                  className="w-12 h-12 rounded-xl object-cover shadow-lg shadow-purple-500/30"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <Disc3 className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-white">{entity.name || 'DJ Live'}</h1>
                <p className="text-sm text-purple-300">
                  {sessionData.venue ? `@ ${sessionData.venue}` : sessionData.session_name || 'Live Session'}
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-3xl font-light text-white tabular-nums">
                {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-sm text-white/80 font-medium">
                {currentTime.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
              </p>
            </div>
          </div>

          {/* Main Content - Different layout for 4:3 vs 16:9 */}
          <div className={`flex-1 grid ${is16by9 ? 'grid-cols-3' : 'grid-cols-1'} gap-4 min-h-0`}>
            {/* Now Playing */}
            <div className={is16by9 ? 'col-span-2' : ''}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                <h2 className="text-xl font-semibold text-green-400">NOW PLAYING</h2>
              </div>
              
              <AnimatePresence mode="wait">
                {nowPlaying ? (
                  <motion.div
                    key={nowPlaying.request_id || nowPlaying.dance_name}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className={`bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-xl ${is16by9 ? 'p-8 h-[calc(100%-40px)]' : 'p-6'}`}
                  >
                    <div className={`${is16by9 ? 'h-full flex flex-col justify-center' : ''}`}>
                      <motion.h3 
                        className={`font-bold text-white mb-4 leading-tight ${is16by9 ? 'text-6xl' : 'text-4xl'}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        {nowPlaying.dance_name}
                      </motion.h3>
                      
                      <div className={`flex flex-wrap items-center gap-4 ${is16by9 ? 'text-2xl' : 'text-lg'}`}>
                        {nowPlaying.level && (
                          <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full">
                            {nowPlaying.level}
                          </span>
                        )}
                        {nowPlaying.count && (
                          <span className="text-slate-400">
                            {nowPlaying.count} Count
                          </span>
                        )}
                        {nowPlaying.requester_name && (
                          <span className="text-slate-400">
                            Requested by {nowPlaying.requester_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 flex items-center justify-center ${is16by9 ? 'p-8 h-[calc(100%-40px)]' : 'p-6 py-12'}`}
                  >
                    <div className="text-center">
                      <Disc3 className={`text-slate-600 mx-auto mb-4 ${is16by9 ? 'w-20 h-20' : 'w-16 h-16'}`} />
                      <p className={`text-slate-400 ${is16by9 ? 'text-3xl' : 'text-2xl'}`}>Waiting for next dance...</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right Column (16:9) or Bottom Section (4:3) - QR first, then queue */}
            <div className={`flex ${is16by9 ? 'flex-col h-full' : 'flex-row items-start'} gap-4`}>
              {/* QR Code - ALWAYS VISIBLE AT TOP, cannot shrink */}
              <div className={`bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-700/50 flex-shrink-0 ${is16by9 ? 'p-4' : 'p-3'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <QrCode className="w-4 h-4 text-green-400" />
                  <h2 className="text-sm font-semibold text-green-400">REQUEST A DANCE</h2>
                </div>
                
                <div className={`flex items-center ${is16by9 ? 'gap-4' : 'gap-3'}`}>
                  <div className="bg-white p-2 rounded-xl shadow-lg flex-shrink-0">
                    <QRCodeSVG 
                      value={livePageUrl}
                      size={is16by9 ? 120 : 70}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-300 mb-1">
                      Scan to request!
                    </p>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Users className="w-4 h-4" />
                      <span className="text-sm">{requestCount} requests</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Up Next - Scrollable within remaining space */}
              <div className={is16by9 ? 'flex-1 min-h-0 flex flex-col' : 'flex-1'}>
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                  <Clock className="w-5 h-5 text-orange-400" />
                  <h2 className="text-lg font-semibold text-orange-400">UP NEXT</h2>
                </div>
                
                <div className={`${is16by9 ? 'flex-1 min-h-0 overflow-y-auto space-y-2 pr-1' : 'grid grid-cols-2 gap-2'}`} style={is16by9 ? { scrollbarWidth: 'thin' } : {}}>
                  <AnimatePresence mode="popLayout">
                    {upNext.length > 0 ? (
                      upNext.slice(0, is16by9 ? 10 : 2).map((dance, index) => (
                        <motion.div
                          key={dance.request_id || `dance-${index}`}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ delay: index * 0.05 }}
                          layout
                          className="bg-slate-800/70 backdrop-blur-sm rounded-xl p-3 border border-orange-500/30"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg font-bold text-orange-400">{index + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-lg font-semibold text-white truncate">{dance.dance_name}</h4>
                              <p className="text-sm text-slate-300 truncate">
                                {dance.level && <span className="text-orange-300">{dance.level}</span>}
                                {dance.requester_name && <span> • {dance.requester_name}</span>}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className={`bg-slate-800/50 rounded-xl p-6 text-center border border-orange-500/20 ${is16by9 ? '' : 'col-span-2'}`}>
                        <p className="text-lg text-slate-400">No upcoming dances</p>
                      </div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
          
          {/* Event Schedule - Shows when enabled */}
          {hasSchedule && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-cyan-400" />
                  <h2 className="text-lg font-semibold text-cyan-400">EVENT SCHEDULE</h2>
                </div>
                {scheduleData?.event_date && (
                  <span className="text-sm text-slate-400">
                    {new Date(scheduleData.event_date + 'T00:00:00').toLocaleDateString('en-GB', { 
                      weekday: 'short', 
                      day: 'numeric', 
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                )}
              </div>
              <div className={`grid gap-3 ${is16by9 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                {scheduleItems.slice(0, is16by9 ? 4 : 3).map((item, index) => {
                  const now = currentTime.getHours() * 60 + currentTime.getMinutes();
                  const isCurrent = item.totalMinutes <= now && item.totalMinutes >= now - 30;
                  const isPast = item.totalMinutes < now - 5;
                  
                  return (
                    <motion.div
                      key={item.item_id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: isPast ? 0.5 : 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`rounded-xl p-3 border ${
                        isCurrent 
                          ? 'bg-cyan-500/20 border-cyan-500/50' 
                          : 'bg-slate-800/70 border-slate-700/50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`text-lg font-bold flex-shrink-0 ${isCurrent ? 'text-cyan-400' : 'text-slate-400'}`}>
                          {item.time}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold text-sm leading-tight ${isCurrent ? 'text-cyan-100' : 'text-white'}`}>
                            {item.title}
                          </h4>
                          {item.description && (
                            <p className="text-xs text-slate-400 truncate">{item.description}</p>
                          )}
                        </div>
                        {isCurrent && (
                          <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse mt-1 flex-shrink-0" />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-lg font-medium text-white/90">Line Dance Central</p>
            <p className="text-lg font-medium text-orange-400">{playedCount} dances played</p>
          </div>
        </div>
      </div>
    </div>
  );
}
